import React, { useState, useRef, useEffect } from 'react';
import { translate } from 'react-i18next';
import CBLabelAndCheckbox from '../../shared/CBLabelAndCheckbox';
import { T, inputStyle, selectStyle, inputLabelStyle } from './chainSettingsV2Styles';

/*
 * Invoicing settings for one care service.
 *
 * The settings are a sequence, not a list: work accrues, an invoice is built
 * from it, and the invoice is transferred. Each step is one collapsible
 * section whose collapsed header states what is currently selected, so the
 * whole configuration can be read without opening anything.
 *
 * Every field still carries its full Finnish explanation; the explanation now
 * sits behind a "?" toggle instead of competing with the field for attention.
 */

var OPTION_STYLE = { backgroundColor: '#1a1a2e', color: '#fff' };

/* Short labels live next to the option they describe, so a wording change
 * cannot leave the header summary describing a different choice than the
 * dropdown. Both are translation keys — copy belongs in the locale files. */
var INTERVAL_OPTIONS = [
  { value: 'manual', labelKey: 'interval_manual', summaryKey: 'summaryInterval_manual' },
  { value: 'daily', labelKey: 'interval_daily', summaryKey: 'summaryInterval_daily' },
  { value: 'weekly', labelKey: 'interval_weekly', summaryKey: 'summaryInterval_weekly' },
  { value: 'biweekly', labelKey: 'interval_biweekly', summaryKey: 'summaryInterval_biweekly' },
  { value: 'bimonthly', labelKey: 'interval_bimonthly', summaryKey: 'summaryInterval_bimonthly' },
  { value: 'monthly', labelKey: 'interval_monthly', summaryKey: 'summaryInterval_monthly' },
  { value: 'quarterly', labelKey: 'interval_quarterly', summaryKey: 'summaryInterval_quarterly' },
];

var EXPORT_INTERVAL_VALUES = ['daily', 'weekly', 'biweekly', 'bimonthly', 'monthly'];

var OUTSTANDING_OPTIONS = [
  {
    value: 'sweep_all',
    labelKey: 'outstanding_sweep_all',
    summaryKey: 'summaryOutstanding_sweep_all',
  },
  {
    value: 'period_only',
    labelKey: 'outstanding_period_only',
    summaryKey: 'summaryOutstanding_period_only',
  },
  {
    value: 'sweep_with_review',
    labelKey: 'outstanding_sweep_with_review',
    summaryKey: 'summaryOutstanding_sweep_with_review',
  },
];

var DETAIL_OPTIONS = [
  { value: 'summary', labelKey: 'detailSummary', summaryKey: 'summaryDetail_summary' },
  { value: 'detailed', labelKey: 'detailDetailed', summaryKey: 'summaryDetail_detailed' },
];

var EXPORT_MODE_OPTIONS = [
  { value: 'manual', labelKey: 'exportMode_manual', summaryKey: 'summaryExportMode_manual' },
  { value: 'immediate', labelKey: 'exportMode_immediate', summaryKey: 'summaryExportMode_immediate' },
  { value: 'scheduled', labelKey: 'exportMode_scheduled', summaryKey: null },
];

var WEEKDAY_INTERVALS = ['weekly', 'biweekly'];
var MONTHDAY_INTERVALS = ['monthly', 'quarterly'];

var SEPARATOR = ' · ';

/* Every help text on the page, so "show all" has something definite to open. */
var HELP_FIELDS = [
  'netvisor_customer_number', 'assigned_workshop', 'invoicing_interval',
  'invoicing_anchor_day', 'invoice_on_month_end', 'invoicing_paused_until',
  'outstanding_policy', 'invoice_detail_level', 'invoice_per_purchase_invoice',
  'margins', 'requires_approval', 'export_mode', 'export_interval',
  'export_anchor_day', 'payment_term_days_override',
];

/* Sections that own a field the save handler can reject. Its error message is
 * shared by all three numeric fields, so both are opened rather than guessing. */
var SECTIONS_WITH_VALIDATED_FIELDS = [2, 3];

function findOption(options, value) {
  for (var i = 0; i < options.length; i += 1) {
    if (options[i].value === value) return options[i];
  }
  return options[0];
}

function label(extra) {
  return Object.assign({}, inputLabelStyle, { fontSize: 11 }, extra || {});
}

/* Muted white and the input-border white, both already in the theme's overlay
 * family — the help block reads as an aside without introducing a colour. */
var MUTED = 'rgba(255, 255, 255, 0.6)';

var styles = {
  card: {
    backgroundColor: T.card,
    border: '1px solid ' + T.border,
    borderRadius: T.radius,
    marginTop: 10,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    width: '100%',
    padding: '12px 14px',
    background: 'none',
    border: 'none',
    borderRadius: T.radius,
    textAlign: 'left',
    cursor: 'pointer',
    color: T.textPrimary,
    font: 'inherit',
  },
  badge: {
    flex: '0 0 auto',
    width: 23,
    height: 23,
    borderRadius: T.radiusSm,
    backgroundColor: T.cardHover,
    color: T.textPrimary,
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  headerText: { flex: '1 1 auto', minWidth: 0, display: 'block' },
  headerTitle: {
    display: 'block',
    fontSize: 13,
    fontWeight: 700,
    color: T.textPrimary,
    margin: 0,
    letterSpacing: '.02em',
  },
  headerSummary: {
    display: 'block',
    fontSize: 12.5,
    color: MUTED,
    margin: '2px 0 0 0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  caret: {
    flex: '0 0 auto',
    color: MUTED,
    fontSize: 12,
    marginTop: 5,
    transition: 'transform .15s ease',
  },
  body: { padding: '0 14px 14px 14px' },
  sectionLead: {
    color: T.textMuted,
    fontSize: 11,
    margin: '0 0 12px 0',
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 12,
  },
  field: { display: 'flex', flexDirection: 'column' },
  input: Object.assign({}, inputStyle, { height: 32, fontSize: 12 }),
  select: Object.assign({}, selectStyle, { height: 32, fontSize: 12 }),
  labelRow: { display: 'flex', alignItems: 'center', gap: 6 },
  helpToggle: {
    flex: '0 0 auto',
    width: 16,
    height: 16,
    lineHeight: '16px',
    padding: 0,
    borderRadius: '50%',
    border: '1px solid ' + T.inBdr,
    backgroundColor: 'transparent',
    color: MUTED,
    fontSize: 10,
    fontWeight: 700,
    cursor: 'pointer',
  },
  helpBlock: {
    margin: '6px 0 0 0',
    padding: '7px 10px',
    fontSize: 11,
    lineHeight: 1.45,
    color: T.textSecondary,
    backgroundColor: T.inBg,
    borderLeft: '2px solid ' + T.inBdr,
    borderRadius: T.radiusSm,
  },
  globalToggle: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 16,
    padding: 0,
    background: 'none',
    border: 'none',
    color: T.textSecondary,
    font: 'inherit',
    cursor: 'pointer',
  },
  notice: {
    color: '#FFE082',
    fontSize: 11,
    margin: '6px 0 0 0',
    fontStyle: 'italic',
  },
};

function formatDate(iso) {
  if (!iso) return null;
  var parts = String(iso).split('-');
  if (parts.length !== 3) return iso;
  return Number(parts[2]) + '.' + Number(parts[1]) + '.' + parts[0];
}

/* A pause only matters while it is still ahead; a date that has passed is
 * history and saying "paused until" about it would be a lie. */
function isFuture(isoDate) {
  if (!isoDate) return false;
  var today = new Date();
  var midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  var parts = String(isoDate).split('-');
  if (parts.length !== 3) return false;
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])) > midnight;
}

/* The anchor day is only asked for on the rhythms that have one — the same
 * condition the field itself is rendered under. */
function anchorApplies(interval) {
  return interval !== 'manual' && interval !== 'daily' && interval !== 'bimonthly';
}

function intervalOf(form) {
  return form.invoicing_interval || 'monthly';
}

function summaryRhythm(form, t) {
  var interval = intervalOf(form);
  var segment = t(findOption(INTERVAL_OPTIONS, interval).summaryKey);

  if (anchorApplies(interval)) {
    var day = form.invoicing_anchor_day || 1;
    segment += ', ' + (WEEKDAY_INTERVALS.indexOf(interval) !== -1
      ? t('weekdayPlain_' + day)
      : t('summaryAnchorMonthDay', { day: day }));
  }
  if (form.invoice_on_month_end) {
    segment += ' + ' + t('summaryMonthEnd');
  }

  var parts = [segment];
  if (isFuture(form.invoicing_paused_until)) {
    parts.push(t('summaryPaused', { date: formatDate(form.invoicing_paused_until) }));
  }
  return parts.join(SEPARATOR);
}

function summaryContent(form, t) {
  var hasOwnMargins = !!form.work_hourly_surcharge_override
    || !!form.parts_markup_percentage_override;
  return [
    t(findOption(DETAIL_OPTIONS, form.invoice_detail_level || 'summary').summaryKey),
    t(form.invoice_per_purchase_invoice ? 'summaryPerPurchaseInvoice' : 'summaryPooledInvoice'),
    t(findOption(OUTSTANDING_OPTIONS, form.outstanding_policy || 'sweep_all').summaryKey),
    t(hasOwnMargins ? 'summaryOwnMargins' : 'summaryChainMargins'),
  ].join(SEPARATOR);
}

function summaryDispatch(form, t, chainPaymentTerm) {
  var mode = form.export_mode || 'manual';
  var transfer;
  if (mode === 'scheduled') {
    var exportInterval = form.export_interval || 'weekly';
    transfer = t(findOption(INTERVAL_OPTIONS, exportInterval).summaryKey).toLowerCase();
  } else {
    transfer = t(findOption(EXPORT_MODE_OPTIONS, mode).summaryKey);
  }

  var term = form.payment_term_days_override
    ? t('summaryPaymentTermDays', { days: form.payment_term_days_override })
    : t('summaryPaymentTermChainDefault', { days: chainPaymentTerm || 14 });

  return [
    t(form.requires_approval ? 'summaryApprovalRequired' : 'summaryApprovalNotRequired'),
    t('summaryExport', { value: transfer }),
    t('summaryPaymentTermValue', { value: term }),
  ].join(SEPARATOR);
}

/* One line summarising a customer's rhythm, for the customer list. Same
 * vocabulary as the settings panel below, so the list and the panel never
 * describe the same customer differently. */
export function careServiceRhythmLine(careService, t) {
  if (!careService) return '';
  var cs = careService;
  if (cs.is_active === false) return t('rowEnded');
  if (isFuture(cs.invoicing_paused_until)) {
    return t('rowPaused', { date: formatDate(cs.invoicing_paused_until) });
  }

  var interval = cs.invoicing_interval || 'monthly';
  if (interval === 'manual') return t('interval_manual');

  var when = t('interval_' + interval);
  if (cs.next_invoicing_date) {
    return when + SEPARATOR + t('rowNext', { date: formatDate(cs.next_invoicing_date) });
  }
  return when;
}

function CareServiceInvoicingSettings(props) {
  var t = props.t;
  var form = props.csForm || {};
  var update = props.updateCsForm;
  var interval = intervalOf(form);
  var exportMode = form.export_mode || 'manual';
  var exportInterval = form.export_interval || 'weekly';
  var formError = props.csFormError;

  var openSectionsState = useState({});
  var openSections = openSectionsState[0];
  var setOpenSections = openSectionsState[1];

  var openHelpState = useState({});
  var openHelp = openHelpState[0];
  var setOpenHelp = openHelpState[1];

  var showAllHelpState = useState(false);
  var showAllHelp = showAllHelpState[0];
  var setShowAllHelp = showAllHelpState[1];

  var firstErrorSection = useRef(null);

  /* A collapsed section must never hide a rejected field. The save handler
   * reports one shared message for all three numeric fields, so every section
   * that owns one is opened rather than guessing which was rejected. */
  useEffect(function () {
    if (!formError) return;
    setOpenSections(function (previous) {
      var next = Object.assign({}, previous);
      SECTIONS_WITH_VALIDATED_FIELDS.forEach(function (n) { next[n] = true; });
      return next;
    });
    if (firstErrorSection.current && firstErrorSection.current.scrollIntoView) {
      firstErrorSection.current.scrollIntoView({ block: 'nearest' });
    }
  }, [formError]);

  var uid = 'cs-' + (props.careService && props.careService.id ? props.careService.id : 'new');

  function toggleSection(number) {
    return function () {
      setOpenSections(function (previous) {
        var next = Object.assign({}, previous);
        next[number] = !previous[number];
        return next;
      });
    };
  }

  function toggleHelp(field) {
    return function () {
      setOpenHelp(function (previous) {
        var next = Object.assign({}, previous);
        next[field] = !previous[field];
        return next;
      });
    };
  }

  function toggleAllHelp() {
    var next = !showAllHelp;
    setShowAllHelp(next);
    var expanded = {};
    if (next) {
      HELP_FIELDS.forEach(function (field) { expanded[field] = true; });
    }
    setOpenHelp(expanded);
  }

  function set(field) {
    return function (e) { update(field, e.target.value); };
  }
  function toggle(field) {
    return function () { update(field, !form[field]); };
  }

  function helpButton(field) {
    var open = !!openHelp[field];
    return (
      <button
        type="button"
        className="cs-help-toggle"
        style={styles.helpToggle}
        aria-expanded={open}
        aria-controls={uid + '-help-' + field}
        aria-label={open ? t('hideHelpAria') : t('showHelpAria')}
        onClick={toggleHelp(field)}
      >
        ?
      </button>
    );
  }

  function fieldLabel(field, text) {
    return (
      <span style={styles.labelRow}>
        <label style={label({ marginBottom: 0 })}>{text}</label>
        {helpButton(field)}
      </span>
    );
  }

  /* Rendered even while closed, so the button's aria-controls always resolves
   * to a real element; `hidden` is what actually collapses it. */
  function helpText(field, text) {
    return (
      <p
        id={uid + '-help-' + field}
        hidden={!openHelp[field]}
        style={styles.helpBlock}
      >
        {text}
      </p>
    );
  }

  function checkboxRow(field, labelText, checked, onChange, help) {
    return (
      <div>
        <div style={styles.labelRow}>
          <CBLabelAndCheckbox labelText={labelText} checked={checked} onChange={onChange} />
          {helpButton(field)}
        </div>
        {helpText(field, help)}
      </div>
    );
  }

  function anchorControl(field, forInterval) {
    if (WEEKDAY_INTERVALS.indexOf(forInterval) !== -1) {
      return (
        <select value={form[field] || 1} onChange={set(field)} style={styles.select}>
          {[1, 2, 3, 4, 5, 6, 7].map(function (d) {
            return <option style={OPTION_STYLE} key={d} value={d}>{t('weekday_' + d)}</option>;
          })}
        </select>
      );
    }
    if (MONTHDAY_INTERVALS.indexOf(forInterval) !== -1) {
      var days = [];
      for (var d = 1; d <= 28; d += 1) days.push(d);
      return (
        <select value={form[field] || 1} onChange={set(field)} style={styles.select}>
          {days.map(function (day) {
            return <option style={OPTION_STYLE} key={day} value={day}>{day}.</option>;
          })}
        </select>
      );
    }
    return null;
  }

  function section(number, titleKey, summary, children, headerRef) {
    var open = !!openSections[number];
    var bodyId = uid + '-section-' + number;
    return (
      <div style={styles.card} ref={headerRef}>
        <button
          type="button"
          className="cs-accordion-header"
          style={styles.header}
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={toggleSection(number)}
        >
          <span style={styles.badge}>{number}</span>
          <span style={styles.headerText}>
            <span style={styles.headerTitle}>{t(titleKey)}</span>
            {!open && (
              <span style={styles.headerSummary} title={summary}>{summary}</span>
            )}
          </span>
          <i
            className="pi pi-chevron-right"
            style={Object.assign({}, styles.caret, {
              transform: open ? 'rotate(90deg)' : 'none',
            })}
          />
        </button>
        {open && (
          <div id={bodyId} role="region" style={styles.body}>{children}</div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* --- Perustiedot --- */}
      <div style={styles.grid2}>
        <div style={styles.field}>
          {fieldLabel('netvisor_customer_number', t('netvisorCustomerNumber'))}
          <input
            type="text"
            value={form.netvisor_customer_number || ''}
            onChange={set('netvisor_customer_number')}
            style={Object.assign({}, styles.input, { marginTop: 4 })}
          />
          {helpText('netvisor_customer_number', t('helpNetvisorCustomerNumber'))}
        </div>
        <div style={styles.field}>
          {fieldLabel('assigned_workshop', t('csAssignedWorkshop'))}
          <select
            value={form.assigned_workshop_id || ''}
            onChange={set('assigned_workshop_id')}
            style={Object.assign({}, styles.select, { marginTop: 4 })}
          >
            <option style={OPTION_STYLE} value="">{t('csNoWorkshop')}</option>
            {(props.chainMemberships || []).map(function (m) {
              var wsId = m.workshop && m.workshop.id ? m.workshop.id : m.workshop;
              var wsName = m.workshop && m.workshop.name ? m.workshop.name : ('Korjaamo #' + wsId);
              return <option style={OPTION_STYLE} key={m.id} value={wsId}>{wsName}</option>;
            })}
          </select>
          {helpText('assigned_workshop', t('helpAssignedWorkshop'))}
        </div>
      </div>

      <button
        type="button"
        className="cs-help-toggle-all"
        style={styles.globalToggle}
        aria-pressed={showAllHelp}
        onClick={toggleAllHelp}
      >
        <CBLabelAndCheckbox labelText={t('showAllHelp')} checked={showAllHelp} />
      </button>

      {/* --- 1. Milloin koontilasku muodostetaan --- */}
      {section(1, 'groupRhythmTitle', summaryRhythm(form, t), (
        <div>
          <p style={styles.sectionLead}>{t('groupRhythmLead')}</p>

          <div style={styles.grid2}>
            <div style={styles.field}>
              {fieldLabel('invoicing_interval', t('invoicingInterval'))}
              <select
                value={interval}
                onChange={set('invoicing_interval')}
                style={Object.assign({}, styles.select, { marginTop: 4 })}
              >
                {INTERVAL_OPTIONS.map(function (option) {
                  return (
                    <option style={OPTION_STYLE} key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  );
                })}
              </select>
              {helpText('invoicing_interval', t('intervalHelp_' + interval))}
            </div>

            {anchorApplies(interval) && (
              <div style={styles.field}>
                {fieldLabel('invoicing_anchor_day', t('invoicingAnchorDay'))}
                <div style={{ marginTop: 4 }}>
                  {anchorControl('invoicing_anchor_day', interval)}
                </div>
                {helpText('invoicing_anchor_day', t('helpInvoicingAnchorDay'))}
              </div>
            )}
          </div>

          <div style={{ marginTop: 12 }}>
            {checkboxRow(
              'invoice_on_month_end',
              t('csInvoiceOnMonthEnd'),
              form.invoice_on_month_end || false,
              toggle('invoice_on_month_end'),
              t('helpInvoiceOnMonthEnd'),
            )}
          </div>

          <div style={{ marginTop: 12, maxWidth: 260 }}>
            {fieldLabel('invoicing_paused_until', t('csInvoicingPausedUntil'))}
            <input
              type="date"
              value={form.invoicing_paused_until || ''}
              onChange={function (e) { update('invoicing_paused_until', e.target.value || null); }}
              style={Object.assign({}, styles.input, { marginTop: 4 })}
            />
            {helpText('invoicing_paused_until', t('helpInvoicingPausedUntil'))}
          </div>
        </div>
      ))}

      {/* --- 2. Mitä laskulle otetaan mukaan --- */}
      {section(2, 'groupContentTitle', summaryContent(form, t), (
        <div>
          <p style={styles.sectionLead}>{t('groupContentLead')}</p>

          <div style={styles.grid2}>
            <div style={styles.field}>
              {fieldLabel('outstanding_policy', t('csOutstandingPolicy'))}
              <select
                value={form.outstanding_policy || 'sweep_all'}
                onChange={set('outstanding_policy')}
                style={Object.assign({}, styles.select, { marginTop: 4 })}
              >
                {OUTSTANDING_OPTIONS.map(function (option) {
                  return (
                    <option style={OPTION_STYLE} key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  );
                })}
              </select>
              {helpText(
                'outstanding_policy',
                t('outstandingHelp_' + (form.outstanding_policy || 'sweep_all')),
              )}
            </div>

            <div style={styles.field}>
              {fieldLabel('invoice_detail_level', t('invoiceDetailLevel'))}
              <select
                value={form.invoice_detail_level || 'summary'}
                onChange={set('invoice_detail_level')}
                style={Object.assign({}, styles.select, { marginTop: 4 })}
              >
                {DETAIL_OPTIONS.map(function (option) {
                  return (
                    <option style={OPTION_STYLE} key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  );
                })}
              </select>
              {helpText(
                'invoice_detail_level',
                t('detailHelp_' + (form.invoice_detail_level || 'summary')),
              )}
            </div>
          </div>

          <div style={{ marginTop: 12 }}>
            {checkboxRow(
              'invoice_per_purchase_invoice',
              t('csInvoicePerPurchaseInvoice'),
              form.invoice_per_purchase_invoice || false,
              toggle('invoice_per_purchase_invoice'),
              t('helpInvoicePerPurchaseInvoice'),
            )}
          </div>

          <div style={{ marginTop: 14 }}>
            {fieldLabel('margins', t('csMarginOverridesLabel'))}
            {helpText('margins', t('csMarginOverridesHelp'))}
          </div>
          <div style={Object.assign({}, styles.grid2, { marginTop: 8 })}>
            <div style={styles.field}>
              <label style={label()}>{t('csWorkSurchargeOverride')}</label>
              <input
                type="text"
                inputMode="decimal"
                className="white-placeholder-input"
                placeholder={t('chainDefault')}
                value={form.work_hourly_surcharge_override || ''}
                onChange={set('work_hourly_surcharge_override')}
                style={styles.input}
              />
            </div>
            <div style={styles.field}>
              <label style={label()}>{t('csPartsMarkupOverride')}</label>
              <input
                type="text"
                inputMode="decimal"
                className="white-placeholder-input"
                placeholder={t('chainDefault')}
                value={form.parts_markup_percentage_override || ''}
                onChange={set('parts_markup_percentage_override')}
                style={styles.input}
              />
            </div>
          </div>
        </div>
      ), firstErrorSection)}

      {/* --- 3. Milloin lasku siirtyy Netvisoriin --- */}
      {section(3, 'groupDispatchTitle', summaryDispatch(form, t, props.chainPaymentTerm), (
        <div>
          <p style={styles.sectionLead}>{t('groupDispatchLead')}</p>

          {checkboxRow(
            'requires_approval',
            t('csRequiresApproval'),
            form.requires_approval || false,
            toggle('requires_approval'),
            form.requires_approval ? t('helpRequiresApprovalOn') : t('helpRequiresApprovalOff'),
          )}

          <div style={Object.assign({}, styles.grid2, { marginTop: 12 })}>
            <div style={styles.field}>
              {fieldLabel('export_mode', t('csExportMode'))}
              <select
                value={exportMode}
                onChange={set('export_mode')}
                style={Object.assign({}, styles.select, { marginTop: 4 })}
              >
                {EXPORT_MODE_OPTIONS.map(function (option) {
                  return (
                    <option style={OPTION_STYLE} key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  );
                })}
              </select>
              {helpText('export_mode', t('exportModeHelp_' + exportMode))}
            </div>

            {exportMode === 'scheduled' && (
              <div style={styles.field}>
                {fieldLabel('export_interval', t('csExportInterval'))}
                <select
                  value={exportInterval}
                  onChange={set('export_interval')}
                  style={Object.assign({}, styles.select, { marginTop: 4 })}
                >
                  {EXPORT_INTERVAL_VALUES.map(function (value) {
                    return (
                      <option style={OPTION_STYLE} key={value} value={value}>
                        {t(findOption(INTERVAL_OPTIONS, value).labelKey)}
                      </option>
                    );
                  })}
                </select>
                {helpText('export_interval', t('helpExportInterval'))}
              </div>
            )}
          </div>

          {exportMode === 'scheduled'
            && (WEEKDAY_INTERVALS.indexOf(exportInterval) !== -1
                || MONTHDAY_INTERVALS.indexOf(exportInterval) !== -1) && (
            <div style={{ marginTop: 12, maxWidth: 260 }}>
              {fieldLabel('export_anchor_day', t('csExportAnchorDay'))}
              <div style={{ marginTop: 4 }}>
                {anchorControl('export_anchor_day', exportInterval)}
              </div>
              {helpText('export_anchor_day', t('helpExportAnchorDay'))}
            </div>
          )}

          <div style={{ marginTop: 12, maxWidth: 260 }}>
            {fieldLabel('payment_term_days_override', t('csPaymentTermOverride'))}
            <input
              type="text"
              inputMode="numeric"
              className="white-placeholder-input"
              placeholder={t('chainDefaultDays', { days: props.chainPaymentTerm || 14 })}
              value={form.payment_term_days_override || ''}
              onChange={set('payment_term_days_override')}
              style={Object.assign({}, styles.input, { marginTop: 4 })}
            />
            {helpText('payment_term_days_override', t('helpPaymentTermOverride'))}
          </div>

          {props.blockExternalWrites && (
            <p style={styles.notice}>{t('csAutoExportBlockedNote')}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default translate('ChainSettingsView')(CareServiceInvoicingSettings);
